import { useMemo } from "react";
import {
  Activity,
  ArrowRight,
  Clock3,
  Coins,
  Plus,
  Receipt,
  Shield,
  ShieldCheck,
  Wallet,
  Zap,
} from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useDeviceApprovals, useIsSignedIn } from "@empite/session";
import type {
  DeviceApproval,
  Money,
  SessionInfo,
  UsagePage,
  Wallet as WalletBalance,
} from "@empite/api-client";
import { useCustomerApplications } from "../applications";
import type { PortalApplication } from "../applications";
import { useUsageAcross, useWalletsAcross } from "../billing";
import { AppIcon } from "../ui/app-icon";
import { Button } from "../ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { formatCount, formatDateTime, humanizeState, relativeTime } from "../ui/format";
import { MarketingShell } from "../ui/marketing-shell";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { UiLink } from "../ui/router";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/status-pill";
import { StatCard } from "../ui/stat-card";
import { UsageChart } from "../ui/usage-chart";
import { WalletRow } from "../ui/wallet-row";
import {
  CHART_DAYS,
  appIdentity,
  chartPoints,
  chartSeries,
  formatTotals,
  sumByCurrency,
} from "./billing-common";
import type { ChartInput } from "./billing-common";
import { errorMessage } from "./guards";

/**
 * The portal's front door: the design's marketing page for a visitor, its
 * /overview dashboard for a signed-in customer.
 *
 * The two states are different frames as well as different content — the
 * marketing page carries its own header and footer, the dashboard renders
 * inside `AppShell` — so `screens/layout.tsx` decides which one wraps this
 * route from the same session this component reads.
 *
 * Design panels with nothing behind them are dropped rather than filled in:
 * the session card's "signed in from" address (the platform's `SessionInfo`
 * carries no address) and the request count's week-on-week trend (nothing
 * reports a previous period) are both gone. An invented number on a dashboard
 * is indistinguishable from a measured one.
 */
export function Home() {
  const { ready, session } = useIsSignedIn();

  if (!ready) {
    return (
      <div className="mx-auto w-full max-w-[1440px] space-y-4 px-4 py-6 sm:px-6 sm:py-8">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
        <span className="sr-only" role="status">
          Loading your account
        </span>
      </div>
    );
  }

  if (!session) {
    return (
      <MarketingShell>
        <MarketingHome />
      </MarketingShell>
    );
  }

  return <Overview session={session} />;
}

/* -- signed in ----------------------------------------------------------- */

interface ApplicationUsage {
  application: PortalApplication;
  page: UsagePage | null;
}

function Overview({ session }: { session: SessionInfo }) {
  const { applications, isPending, error } = useCustomerApplications();
  const wallets = useWalletsAcross(applications);
  const usage = useUsageAcross(applications);
  const approvals = useDeviceApprovals();

  const loading = isPending || wallets.some((query) => query.isPending);
  const failure =
    error ??
    wallets.find((query) => query.error)?.error ??
    usage.find((query) => query.error)?.error;

  const rows = useMemo(() => walletRows(applications, wallets), [applications, wallets]);
  // Grouped by publisher, and only SHOWN grouped when there is more than one:
  // a heading that says the same thing above every row is a heading nobody
  // reads, and most customers hold applications from one company.
  const byCompany = useMemo(() => groupRows(rows), [rows]);
  const usageRows = useMemo(() => usagePages(applications, usage), [applications, usage]);

  const available = sumByCurrency(rows.map((row) => row.wallet?.available));
  const held = sumByCurrency(rows.map((row) => row.wallet?.reserved));
  const charged = sumByCurrency(usageRows.map((row) => row.page?.summary.settled_total));
  const requests = usageRows.reduce((total, row) => total + (row.page?.summary.requests ?? 0), 0);

  const pending = (approvals.data?.device_approvals ?? []).filter(
    (approval) => approval.status === "pending",
  );

  const charts = useMemo(() => chartRows(usageRows), [usageRows]);
  const series = useMemo(() => chartSeries(charts), [charts]);
  const points = useMemo(() => chartPoints(charts), [charts]);
  const charted = usageRows.some((row) => (row.page?.usage.length ?? 0) > 0);

  return (
    <>
      <PageHeader
        title="Your account"
        description="One account for every application in the suite. Credit belongs to the application it was added to."
        actions={
          <>
            <Button href="/usage" variant="secondary">
              View usage
            </Button>
            <Button href="/wallets">
              <Plus className="size-4" aria-hidden />
              Add credit
            </Button>
          </>
        }
      />

      {failure ? (
        <Notice tone="danger" title="Some of your account could not be loaded" className="mb-6">
          {errorMessage(failure)}
        </Notice>
      ) : null}

      {pending.length > 0 ? <PendingDeviceBanner pending={pending} /> : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          accent
          label="Available credit"
          value={loading ? "…" : formatTotals(available)}
          footnote={`across ${formatCount(rows.filter((row) => row.wallet).length)} application ${
            rows.filter((row) => row.wallet).length === 1 ? "wallet" : "wallets"
          }`}
          icon={<Coins className="size-4" aria-hidden />}
        />
        <StatCard
          label="Held for requests in flight"
          value={loading ? "…" : formatTotals(held)}
          footnote={held.length === 0 ? "nothing is currently held" : "released on completion"}
          icon={<Clock3 className="size-4" aria-hidden />}
        />
        <StatCard
          label="Requests"
          value={formatCount(requests)}
          footnote="priced across every application"
          icon={<Zap className="size-4" aria-hidden />}
        />
        <StatCard
          label="Charged"
          value={formatTotals(charged)}
          footnote="settled against your wallets"
          icon={<Receipt className="size-4" aria-hidden />}
        />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Card className="overflow-hidden">
            <CardHeader
              title="Wallets"
              action={
                <UiLink
                  href="/wallets"
                  className="text-sm font-semibold text-brand hover:text-brand-strong"
                >
                  All wallets
                </UiLink>
              }
            />
            {rows.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Wallet className="size-5" aria-hidden />}
                title={loading ? "Loading your wallets" : "No wallets yet"}
                body={
                  loading
                    ? undefined
                    : "A wallet appears here once an application in the suite is funded for your account."
                }
              />
            ) : (
              <ul className="divide-y divide-line-soft">
                {byCompany.map((group) => (
                  <li key={group.slug}>
                    {byCompany.length > 1 ? (
                      <p className="bg-surface-sunken/40 px-5 py-2 text-2xs font-semibold uppercase tracking-wide text-ink-3 sm:px-6">
                        {group.name}
                      </p>
                    ) : null}
                    <ul className="divide-y divide-line-soft">
                      {group.rows.map((row) => (
                        <li key={row.application.id}>
                          <WalletRow
                            app={row.app}
                            available={row.wallet?.available ?? zero(row.wallet)}
                            held={row.wallet?.reserved ?? zero(row.wallet)}
                            status={row.wallet?.status ?? "no wallet"}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
            <CardFooter>
              <p className="text-xs text-ink-3">
                Credits belong to one application — each wallet is spent only on that
                application.
              </p>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader
              title={`Usage — last ${CHART_DAYS} days`}
              action={
                <UiLink
                  href="/usage"
                  className="text-sm font-semibold text-brand hover:text-brand-strong"
                >
                  View usage
                </UiLink>
              }
            />
            <CardBody>
              {charted ? (
                <UsageChart series={series} points={points} />
              ) : (
                <EmptyState
                  size="sm"
                  title="No requests yet"
                  body="Usage appears here once an application makes its first priced request."
                />
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Session" />
            <CardBody className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-medium text-ink-3">Assurance level</span>
                <StatusPill tone="brand">{session.auth_level}</StatusPill>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-medium text-ink-3">Registration state</span>
                <span className="text-xs font-semibold text-ink">
                  {humanizeState(session.registration_state)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-medium text-ink-3">Expires</span>
                <span className="tnum text-xs font-semibold text-ink">
                  {formatDateTime(session.expires_at)}
                </span>
              </div>
            </CardBody>
            <CardFooter>
              <UiLink
                href="/security"
                className="text-sm font-semibold text-brand hover:text-brand-strong"
              >
                Manage sessions
              </UiLink>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader title="Device approvals" />
            <CardBody>
              {pending.length > 0 ? (
                <>
                  <p className="text-sm font-semibold text-ink">
                    {formatCount(pending.length)} installation
                    {pending.length === 1 ? "" : "s"} waiting.
                  </p>
                  <p className="mt-1.5 text-xs text-ink-3">
                    Approve a device before it can sign in on your account.
                  </p>
                  <Button href="/device-approvals" size="sm" className="mt-4 w-full">
                    Review devices
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-ink">Nothing is waiting for you.</p>
                  <p className="mt-1.5 text-xs text-ink-3">
                    New installations will appear here for approval.
                  </p>
                </>
              )}
            </CardBody>
          </Card>

          <Notice title="Credits belong to one application">
            Each application has its own wallet, spent only on that application. Balances are
            never transferred, and are not refundable as credit.
          </Notice>
        </div>
      </div>
    </>
  );
}

function PendingDeviceBanner({ pending }: { pending: DeviceApproval[] }) {
  const first = pending[0]!;
  return (
    <UiLink
      href="/device-approvals"
      className="group mb-6 flex items-center gap-4 rounded-card border border-warning/30 bg-warning-soft/60 px-5 py-4 transition-colors hover:bg-warning-soft"
    >
      <span
        aria-hidden
        className="grid size-9 shrink-0 place-items-center rounded-full bg-surface text-warning-ink shadow-card"
      >
        <Shield className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold text-ink">
          {formatCount(pending.length)} device{pending.length === 1 ? "" : "s"} waiting for your
          approval
        </p>
        <p className="truncate text-xs text-ink-3">
          {first.device_name ?? first.application_name} asked to join{" "}
          {relativeTime(first.created_at)}.
        </p>
      </div>
      <span className="hidden items-center gap-1 text-sm font-semibold text-warning-ink sm:flex">
        Review
        <ArrowRight
          className="size-3.5 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </span>
    </UiLink>
  );
}

/* -- deriving the dashboard's rows ---------------------------------------- */

interface WalletRowData {
  application: PortalApplication;
  app: ReturnType<typeof appIdentity>;
  wallet: WalletBalance | null;
}

interface CompanyRows {
  slug: string;
  name: string;
  rows: WalletRowData[];
}

/**
 * The wallet rows, gathered under the company that publishes each application.
 *
 * Grouped HERE rather than by re-reading the applications list, so the
 * headings and the rows can never describe different sets — they are one
 * array, walked once. Arrival order is kept: the control plane already orders
 * by company and then by application, and re-sorting in the browser would be a
 * second ordering to disagree with it.
 */
function groupRows(rows: WalletRowData[]): CompanyRows[] {
  const groups = new Map<string, CompanyRows>();
  for (const row of rows) {
    const slug = row.application.companySlug;
    const existing = groups.get(slug);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    groups.set(slug, { slug, name: row.application.companyName || slug, rows: [row] });
  }
  return [...groups.values()];
}

/** A 404 answered `null`, which is "no wallet for this application" and an
 *  ordinary row rather than a missing one — the customer still has the
 *  application, and the card is how they fund it. */
function walletRows(
  applications: PortalApplication[],
  queries: Array<UseQueryResult<WalletBalance | null>>,
): WalletRowData[] {
  return applications.map((application, index) => ({
    application,
    app: appIdentity(application, index),
    wallet: queries[index]?.data ?? null,
  }));
}

/** The amount a wallet-less row shows. The currency is the platform default
 *  only because there is no wallet to read one from. */
function zero(wallet: WalletBalance | null): Money {
  return { currency: wallet?.currency ?? "USD", micros: "0" };
}

function usagePages(
  applications: PortalApplication[],
  queries: Array<UseQueryResult<UsagePage | null>>,
): ApplicationUsage[] {
  return applications.map((application, index) => ({
    application,
    page: queries[index]?.data ?? null,
  }));
}

/** The pages as {@link chartSeries} and {@link chartPoints} want them — the
 *  shared shape both this screen and /usage bucket from. */
function chartRows(rows: ApplicationUsage[]): ChartInput[] {
  return rows.map((row) => ({ application: row.application, records: row.page?.usage ?? [] }));
}

/* -- signed out ----------------------------------------------------------- */

const features = [
  {
    icon: Wallet,
    tone: "bg-brand-softer text-brand",
    title: "A wallet per application",
    body: "Each application has its own balance, and credit is spent only where you added it.",
  },
  {
    icon: Activity,
    tone: "bg-success-soft text-success-ink",
    title: "Usage you can read",
    body: "Every request the suite priced, what it cost, and which wallet paid for it.",
  },
  {
    icon: ShieldCheck,
    tone: "bg-warning-soft text-warning-ink",
    title: "Devices you approve",
    body: "A new installation has to be approved by you before it can sign in on your account.",
  },
];

const steps = [
  {
    title: "Create one account",
    body: "Sign in once and every application in the suite knows who you are — no second password, no second profile.",
  },
  {
    title: "Add credit per application",
    body: "Top up the wallet of the application you are using. Credit stays with that application and is never moved.",
  },
  {
    title: "Approve each device",
    body: "The first time an application is installed on a device, it waits for you. Nothing signs in until you say so.",
  },
];

/**
 * The design's hero preview showed a fixture account's real balances. There is
 * no account here to show, and inventing one would be a screenshot of somebody
 * else's money, so the panel keeps the design's shape with the suite's own
 * example applications and says in its caption that it is an example.
 */
const exampleWallets = [
  { name: "Empite Studio", initial: "S", tone: "brand" as const, amount: "$240.00" },
  { name: "Empite Assist", initial: "A", tone: "teal" as const, amount: "$85.50" },
];

function MarketingHome() {
  return (
    <>
      {/* Hero ------------------------------------------------------------- */}
      <section className="relative overflow-hidden">
        <span
          aria-hidden
          className="absolute inset-0 bg-linear-125 from-[color-mix(in_oklab,var(--color-brand)_10%,transparent)] via-transparent to-transparent"
        />
        <span
          aria-hidden
          className="absolute -top-40 -right-32 size-[32rem] rounded-full bg-brand/10 blur-3xl"
        />

        <div className="relative mx-auto grid max-w-[1280px] items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <div>
            <p className="text-2xs font-bold tracking-[0.16em] text-brand uppercase">
              Empite Suite
            </p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-[-0.035em] text-ink sm:text-5xl">
              One account for every application in the suite.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-ink-3">
              Sign in once and every application knows who you are. Each one keeps its own
              wallet and its own usage record.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button href="/register" size="lg">
                Create an account
                <ArrowRight className="size-4" aria-hidden />
              </Button>
              <Button
                href="/sign-in"
                search={{ next: undefined, client_id: undefined }}
                variant="secondary"
                size="lg"
              >
                Sign in
              </Button>
            </div>

            <p className="mt-5 text-xs text-ink-4">
              Signing in here signs you in to every application in the suite.
            </p>
          </div>

          <div className="relative">
            <div className="rounded-panel border border-line bg-surface p-6 shadow-float sm:p-7">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xs font-bold tracking-[0.09em] text-ink-4 uppercase">
                    Available credit
                  </p>
                  <p className="tnum mt-2 font-display text-4xl font-bold tracking-[-0.03em] text-success-ink">
                    $325.50
                  </p>
                </div>
                <StatusPill tone="success" dot pulse>
                  account active
                </StatusPill>
              </div>

              <div className="mt-6 space-y-1 border-t border-line-soft pt-5">
                {exampleWallets.map((wallet) => (
                  <div
                    key={wallet.name}
                    className="flex items-center gap-3 rounded-field px-2 py-2.5"
                  >
                    <AppIcon initial={wallet.initial} tone={wallet.tone} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold text-ink">{wallet.name}</p>
                    </div>
                    <p className="tnum text-md font-bold text-ink">{wallet.amount}</p>
                  </div>
                ))}
              </div>

              <p className="mt-4 border-t border-line-soft pt-4 text-xs text-ink-4">
                An example. Each wallet is spent only on its own application.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features --------------------------------------------------------- */}
      <section className="border-t border-line bg-canvas">
        <div className="mx-auto max-w-[1280px] px-5 py-16 sm:px-8 sm:py-20">
          <div className="grid gap-5 md:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-card border border-line bg-surface p-6 shadow-card transition-shadow duration-200 ease-out-soft hover:shadow-raised"
              >
                <span
                  aria-hidden
                  className={`grid size-11 place-items-center rounded-[13px] ${feature.tone}`}
                >
                  <feature.icon className="size-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold tracking-[-0.01em] text-ink">
                  {feature.title}
                </h3>
                <p className="mt-2 text-base text-ink-3">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works ----------------------------------------------------- */}
      <section id="how-it-works" className="scroll-mt-20 border-t border-line">
        <div className="mx-auto max-w-[1280px] px-5 py-16 sm:px-8 sm:py-20">
          <div className="max-w-2xl">
            <p className="text-2xs font-bold tracking-[0.16em] text-brand uppercase">
              How it works
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-[-0.03em] text-ink">
              Three rules, and nothing hidden behind them.
            </h2>
          </div>

          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <li key={step.title} className="relative">
                <span className="tnum font-display text-4xl font-bold text-brand/25">
                  0{index + 1}
                </span>
                <h3 className="mt-2 font-display text-lg font-bold text-ink">{step.title}</h3>
                <p className="mt-2 text-base text-ink-3">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Security --------------------------------------------------------- */}
      <section id="security" className="scroll-mt-20 border-t border-line bg-canvas">
        <div className="mx-auto grid max-w-[1280px] items-center gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-2">
          <div>
            <p className="text-2xs font-bold tracking-[0.16em] text-brand uppercase">Security</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-[-0.03em] text-ink">
              Every device that signs in, in one place.
            </h2>
            <p className="mt-4 max-w-lg text-base text-ink-3">
              Your account lists every installation it has signed in on — what the machine is,
              when it was last seen, and whether you are still signed in on it. Lost one? Sign it
              out from any browser, and approve the next device before it can join.
            </p>
            <Button
              href="/sign-in"
              search={{ next: "/security", client_id: undefined }}
              variant="secondary"
              className="mt-6"
            >
              See your devices
            </Button>
          </div>

          <div className="rounded-card border border-line bg-surface p-5 shadow-card sm:p-6">
            <div className="space-y-3">
              {[
                { device: "MacBook Pro", signedIn: true, seen: "2 minutes ago" },
                { device: "Pixel 9", signedIn: true, seen: "yesterday" },
                { device: "Windows laptop", signedIn: false, seen: "3 weeks ago" },
              ].map((device) => (
                <div
                  key={device.device}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-soft pb-3 last:border-0 last:pb-0"
                >
                  <span className="text-xs font-semibold text-ink">{device.device}</span>
                  <StatusPill tone={device.signedIn ? "success" : "neutral"}>
                    {device.signedIn ? "signed in" : "signed out"}
                  </StatusPill>
                  <span className="ml-auto text-2xs text-ink-4">{device.seen}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA -------------------------------------------------------------- */}
      <section className="px-5 pb-16 sm:px-8 sm:pb-20">
        <div className="relative mx-auto max-w-[1280px] overflow-hidden rounded-panel bg-linear-100 from-[#2b3e96] to-[#131c46] px-7 py-12 sm:px-12 sm:py-14">
          <span
            aria-hidden
            className="absolute -top-24 -right-16 size-80 rounded-full bg-[#7c97ff]/20 blur-3xl"
          />
          <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-[-0.02em] text-white">
                Ready to use the suite?
              </h2>
              <p className="mt-2 max-w-lg text-base text-[#a9b6e4]">
                Create an account once, and sign in to every application in the suite.
              </p>
            </div>
            <Button
              href="/register"
              size="lg"
              className="bg-white bg-none text-[#16205c] shadow-none hover:bg-white/90"
            >
              Create an account
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
